import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LayoutList, UserX, Sparkles, Clock, RefreshCw,
  Activity, Package, CheckCircle, XCircle, Truck,
  Wrench,
} from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import ServiceDateFilter, { type DatePeriod } from './ServiceDateFilter';
import TargetChasingChart from './TargetChasingChart';

const STATUS_CONFIG = [
  { key: 'pending', totalKey: 'pendingTotal', label: 'Pending', icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' },
  { key: 'inProgress', totalKey: 'inProgressTotal', label: 'In Progress', icon: Activity, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  { key: 'waitingParts', totalKey: 'waitingPartsTotal', label: 'Waiting Parts', icon: Package, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  { key: 'ready', totalKey: 'readyTotal', label: 'Ready', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
  { key: 'notReady', totalKey: 'notReadyTotal', label: 'Not Ready', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  { key: 'delivered', totalKey: 'deliveredTotal', label: 'Delivered', icon: Truck, color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200' },
  { key: 'undelivered', totalKey: 'undeliveredTotal', label: 'Undelivered', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
] as const;

const PERIOD_LABELS: Record<DatePeriod, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This Week',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
  custom: 'Custom Range',
};

/** "New Today" / "New Yesterday" / "New Jul 10 – Jul 17" — adapts to the selected period. */
function formatNewLabel(period: DatePeriod, customRange?: { startDate: string; endDate: string }): string {
  if (period === 'custom' && customRange?.startDate && customRange?.endDate) {
    const fmt = (s: string) => new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `New ${fmt(customRange.startDate)} – ${fmt(customRange.endDate)}`;
  }
  return `New ${PERIOD_LABELS[period]}`;
}

interface ServiceDashboardProps {
  branchId?: string;
}

export default function ServiceDashboard({ branchId }: ServiceDashboardProps) {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<DatePeriod>('today');
  const [customRange, setCustomRange] = useState<{ startDate: string; endDate: string } | undefined>();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['serviceDashboard'] });
    await queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
    await queryClient.invalidateQueries({ queryKey: ['superAdminDashboard'] });
    await queryClient.invalidateQueries({ queryKey: ['targetChasing'] });
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    setTimeout(() => setIsRefreshing(false), 600);
  }, [queryClient]);

  const queryParams = period === 'custom' && customRange
    ? { period: 'custom', startDate: customRange.startDate, endDate: customRange.endDate, branchId }
    : { period, branchId };

  const { data, isLoading } = useQuery({
    queryKey: ['serviceDashboard', queryParams],
    queryFn: () => dashboardApi.getServiceDashboard(queryParams),
    staleTime: 60 * 1000,
    refetchInterval: 90 * 1000,
  });

  const handleDateChange = (newPeriod: DatePeriod, range?: { startDate: string; endDate: string }) => {
    setPeriod(newPeriod);
    if (range) setCustomRange(range);
  };

  const stats: Record<string, number> = data?.stats || {};

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-purple-100 rounded-lg">
            <Wrench className="w-4 h-4 text-purple-600" />
          </div>
          <h2 className="text-sm font-bold text-gray-900">Service Dashboard</h2>
        </div>
        <div className="flex items-center gap-2">
          <ServiceDateFilter value={period} customRange={customRange} onChange={handleDateChange} />
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Refresh dashboard data"
            className="flex items-center gap-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-100 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-50 border border-gray-200"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="text-xs font-medium hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <div className="p-4 space-y-4">
          {/* Top Summary Cards - 4 key metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl p-3 text-white">
              <div className="flex items-center justify-between">
                <LayoutList className="w-5 h-5 opacity-80" />
                <span className="text-2xl font-bold">{stats.totalServices ?? 0}</span>
              </div>
              <p className="text-xs text-white/80 mt-1">Total Services</p>
            </div>

            <div className="bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl p-3 text-white">
              <div className="flex items-center justify-between">
                <Sparkles className="w-5 h-5 opacity-80" />
                <span className="text-2xl font-bold">{stats.newInPeriod ?? 0}</span>
              </div>
              <p className="text-xs text-white/80 mt-1">{formatNewLabel(period, customRange)}</p>
            </div>

            <div className="bg-gradient-to-br from-gray-400 to-gray-600 rounded-xl p-3 text-white">
              <div className="flex items-center justify-between">
                <UserX className="w-5 h-5 opacity-80" />
                <span className="text-2xl font-bold">{stats.unassigned ?? 0}</span>
              </div>
              <p className="text-xs text-white/80 mt-1">Unassigned</p>
              <p className="text-[10px] text-white/60 leading-tight">{stats.unassignedTotal ?? 0} total</p>
            </div>

            <div className="bg-gradient-to-br from-pink-400 to-rose-600 rounded-xl p-3 text-white">
              <div className="flex items-center justify-between">
                <RefreshCw className="w-5 h-5 opacity-80" />
                <span className="text-2xl font-bold">{stats.repeatedService ?? 0}</span>
              </div>
              <p className="text-xs text-white/80 mt-1">Repeated</p>
              <p className="text-[10px] text-white/60 leading-tight">{stats.repeatedServiceTotal ?? 0} total</p>
            </div>
          </div>

          {/* Status Breakdown - compact row */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
            {STATUS_CONFIG.map((item) => {
              const Icon = item.icon;
              const value = stats[item.key] ?? 0;
              return (
                <div
                  key={item.key}
                  className={`${item.bg} ${item.border} border rounded-lg px-2 py-2 text-center`}
                >
                  <Icon className={`w-4 h-4 mx-auto mb-0.5 ${item.color}`} />
                  <div className="text-lg font-bold text-gray-900">{value}</div>
                  {item.totalKey && (
                    <div className="text-[9px] text-gray-400 leading-tight">{stats[item.totalKey] ?? 0} total</div>
                  )}
                  <div className="text-[10px] text-gray-500 font-medium leading-tight">{item.label}</div>
                </div>
              );
            })}
          </div>

          {/* Target Chasing */}
          <TargetChasingChart branchId={branchId} />
        </div>
      )}
    </div>
  );
}
